import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import {
  AccountService,
  CalendarService,
  Channel,
  ChannelApiService,
  ChannelNamePipe,
  ChannelService,
  isChannelExpired as channelIsExpired,
  PlatformService,
  PostService,
  ProfileService,
  Workspace,
  SubscriblyStatusService,
  WorkspaceService,
  environment,
} from '@doddly/core';
import { MenuModule } from 'primeng/menu';
import { MessageService } from 'primeng/api';
import { CalloutComponent } from '../callout/callout.component';
import { IconComponent } from '../icon/icon.component';
import { ChannelAvatarComponent } from '../channel-avatar/channel-avatar.component';
import { ToastModule } from 'primeng/toast';
import { ConfirmDirective } from '../confirm-popover/confirm.directive';
import { switchMap } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';

export interface Context {
  workspaceId: string;
  platform: string;
  order: number;
  accountId: string;
}

export const LOCAL_ORIGIN = 'http://localhost:4200';
const SOCIAL_PLATFORMS = [
  'facebook',
  'instagram',
  'bluesky',
  'threads',
  'pinterest',
  // 'x',  // TODO: add back in when BE is ready
  // 'tiktok', // TODO: add back in when BE is ready
];

@Component({
  selector: 'ly-channels-connection-setup',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    FormsModule,
    ChannelNamePipe,
    MenuModule,
    IconComponent,
    ChannelAvatarComponent,
    ToastModule,
    ConfirmDirective,
    TooltipModule,
    CalloutComponent,
  ],
  templateUrl: './channels-connection-setup.component.html',
  styleUrl: './channels-connection-setup.component.scss',
})
export class ChannelsConnectionSetupComponent {
  workspace: Workspace;
  socialPlatforms = SOCIAL_PLATFORMS;
  channels = computed(() => this.groupChannelsByPlatform());
  profile = computed(() => this.profileService.profile());
  isMobile = computed(() => this.platformService.isMobile());
  subscriblyUnavailable = inject(SubscriblyStatusService).unavailable;
  constructor(
    private accountService: AccountService,
    private workspaceService: WorkspaceService,
    private channelService: ChannelService,
    private channelApiService: ChannelApiService,
    private messageService: MessageService,
    private profileService: ProfileService,
    private calendarService: CalendarService,
    private postService: PostService,
    private platformService: PlatformService,
  ) {
    this.workspace = this.workspaceService.activeWorkspace();
  }

  get redirectURI(): string {
    return location.origin === LOCAL_ORIGIN
      ? `${LOCAL_ORIGIN}/connection`
      : `${environment.subscriblyRedirectURI}/connection`;
  }

  isChannelExpired(channel: Channel): boolean {
    return channelIsExpired(channel);
  }

  isChannelConnected(channel: Channel): boolean {
    return Boolean(channel.subscriblyChannel?.id);
  }

  refreshChannel(channel: Channel) {
    if (this.subscriblyUnavailable()) {
      return;
    }

    this.channelApiService
      .refreshChannel(channel.id)
      .pipe(
        switchMap(() => {
          return this.channelService.getChannels(this.workspace.id);
        }),
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Channel refreshed',
            life: 5000,
            detail: 'Channel refreshed successfully',
          });
        },
      });
  }

  deleteChannel(channel: Channel) {
    this.channelService
      .deleteChannel(channel.id)
      .pipe(
        switchMap(() => {
          const startDate = this.calendarService.startDate().toISOString();
          const endDate = this.calendarService.endDate().toISOString();

          return this.postService.getDoddles(
            this.workspace.id,
            startDate,
            endDate,
          );
        }),
      )
      .subscribe();
  }

  autorizePlatform(platform: string, index: number): void {
    if (this.subscriblyUnavailable()) {
      return;
    }

    const accountId = this.accountService.activeAccount().id;
    const context = JSON.stringify({
      workspaceId: this.workspace.id,
      platform,
      order: index,
      accountId,
    });

    const encodedContextData = window.btoa(context);

    this.channelApiService
      .autorizeChannel({
        workspaceId: this.workspace.id,
        platform,
        redirectURI: this.redirectURI,
        state: encodedContextData,
      })
      .subscribe({
        next: (response) => {
          window.open(response.authorizationURL, '_self');
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Heads-up!',
            detail: `${error.error.errors}`,
            closable: true,
            life: 5000,
          });
        },
      });
  }

  private groupChannelsByPlatform(): { [key: string]: Channel[] } {
    const groupedChannels: { [key: string]: Channel[] } = {};
    this.channelService.channels().forEach((channel) => {
      if (!groupedChannels[channel.platform]) {
        groupedChannels[channel.platform] = [];
      }
      groupedChannels[channel.platform].push(channel);
    });

    return groupedChannels;
  }
}
